import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import fi from "./fi.json";
import ee from "./ee.json";
import se from "./se.json";
import en from "./en.json";
import ru from "./ru.json";

const LOCALES = { fi, ee, se, en, ru };
const DEFAULT_LANG = "fi";
const STORAGE_KEY = "duunari_lang";

/** Map internal codes to ISO 639-1 for the <html lang> attribute and SEO. */
const ISO_LANG = { fi: "fi", ee: "et", se: "sv", en: "en", ru: "ru" };

/** Map internal codes to OG locale format (language_TERRITORY). */
const OG_LOCALE = { fi: "fi_FI", ee: "et_EE", se: "sv_SE", en: "en_US", ru: "ru_RU" };

/** Upsert a <meta> tag by attribute (name or property) and set its content. */
const setMeta = (attr, key, content) => {
  if (typeof document === "undefined" || !content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const LanguageContext = createContext(null);

/** Dot-path lookup: getPath({a:{b:"x"}}, "a.b") -> "x" */
const getPath = (dict, path) => {
  if (!dict) return undefined;
  return path
    .split(".")
    .reduce(
      (acc, part) => (acc && typeof acc === "object" ? acc[part] : undefined),
      dict,
    );
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_LANG;
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      return saved && LOCALES[saved] ? saved : DEFAULT_LANG;
    } catch {
      return DEFAULT_LANG;
    }
  });

  const setLang = useCallback((l) => {
    if (!LOCALES[l]) return;
    setLangState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore quota errors */
    }
  }, []);

  const t = useCallback(
    (key) => {
      const val = getPath(LOCALES[lang], key);
      if (typeof val === "string") return val;
      // Fallback to Finnish
      const fb = getPath(LOCALES[DEFAULT_LANG], key);
      return typeof fb === "string" ? fb : key;
    },
    [lang],
  );

  const tRaw = useCallback(
    (key) => {
      const val = getPath(LOCALES[lang], key);
      if (val !== undefined && val !== null) return val;
      const fb = getPath(LOCALES[DEFAULT_LANG], key);
      return fb !== undefined ? fb : null;
    },
    [lang],
  );

  useEffect(() => {
    if (typeof document === "undefined") return;
    // <html lang="...">
    document.documentElement.lang = ISO_LANG[lang] || DEFAULT_LANG;

    // <title>
    const title = getPath(LOCALES[lang], "seo.title");
    if (typeof title === "string" && title.length > 0) {
      document.title = title;
    }

    // <meta name="description">
    const description = getPath(LOCALES[lang], "seo.description");
    if (typeof description === "string" && description.length > 0) {
      setMeta("name", "description", description);
    }

    // Open Graph + Twitter Card — dynamic per language
    if (typeof title === "string") {
      setMeta("property", "og:title", title);
      setMeta("name", "twitter:title", title);
    }
    if (typeof description === "string") {
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    const ogLocale = OG_LOCALE[lang] || OG_LOCALE[DEFAULT_LANG];
    setMeta("property", "og:locale", ogLocale);
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tRaw, availableLangs: Object.keys(LOCALES) }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Return a passive fallback so hook can be called safely if provider missing
    return {
      lang: DEFAULT_LANG,
      setLang: () => {},
      t: (key) => {
        const fb = getPath(LOCALES[DEFAULT_LANG], key);
        return typeof fb === "string" ? fb : key;
      },
      tRaw: (key) => {
        const fb = getPath(LOCALES[DEFAULT_LANG], key);
        return fb !== undefined ? fb : null;
      },
      availableLangs: Object.keys(LOCALES),
    };
  }
  return ctx;
};
